/**
 * Page admin "Clients boutique B2C" — /parametres/clients-boutique
 *
 * Phase 30b : version minimale
 *  - Liste clients avec stats globales en haut
 *  - Recherche live (email / nom)
 *  - Chips de filtre : Tous / Vérifiés / Non vérifiés / Désactivés
 *  - Actions : ✓ Vérifier manuellement / 🚫 Désactiver|Réactiver / 🗑 Supprimer (anonymise commandes)
 *  - Drawer détail au click sur une ligne : profil + historique commandes
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import {
  ArrowLeft, Search, CheckCircle2, XCircle, Power, Trash2, UserCheck,
  Loader2, Users, Mail, Phone, MapPin, ShoppingBag, Briefcase, Link2, Unlink,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/format";

export default function ShopCustomers() {
  const confirm = useConfirm();
  const [data, setData] = useState({ customers: [], stats: null });
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null | verified | unverified | disabled
  // Phase 101cg (18/02/2026) — Filtre pro / particulier pour éviter que les
  // candidatures pro se noient dans la liste B2C.
  const [typeFilter, setTypeFilter] = useState("particulier"); // "all" | "particulier" | "pro"
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Charge la liste des revendeurs actifs (pour le dropdown de lien)
  useEffect(() => {
    api.get("/resellers")
      .then(({ data: rs }) => setResellers((rs || []).filter((r) => !r.archived)))
      .catch(() => setResellers([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (typeFilter && typeFilter !== "all") params.account_type = typeFilter;
      const { data: resp } = await api.get("/customers", { params });
      setData(resp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  // Recherche live (debounce 300ms) + filtre instantané
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, typeFilter]);

  const onOpenDetail = async (c) => {
    setSelectedId(c.id);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const { data: d } = await api.get(`/customers/${c.id}`);
      setDetail(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      setSelectedId(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const onVerify = async (c) => {
    try {
      await api.put(`/customers/${c.id}`, { email_verified: true });
      toast.success(`${c.email} vérifié manuellement`);
      load();
      if (selectedId === c.id) onOpenDetail(c);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const onToggleDisabled = async (c) => {
    try {
      await api.put(`/customers/${c.id}`, { disabled: !c.disabled });
      toast.success(c.disabled ? "Compte réactivé" : "Compte désactivé");
      load();
      if (selectedId === c.id) onOpenDetail(c);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const onDelete = async (c) => {
    const ok = await confirm({
      title: "Supprimer ce client ?",
      description: (
        <>
          Le compte <strong>{c.email}</strong> sera définitivement supprimé.<br />
          Ses commandes seront <strong>conservées</strong> mais anonymisées
          (nom → "Client supprimé (web)", email → masqué) pour préserver
          la traçabilité comptable.
        </>
      ),
      confirmText: "Supprimer le compte",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data: resp } = await api.delete(`/customers/${c.id}`);
      toast.success(`Client supprimé · ${resp.anonymized_orders} commande(s) anonymisée(s)`);
      setSelectedId(null);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  // Phase 31 : lier / délier un revendeur. resellerId === "" pour délier.
  const onLinkReseller = async (c, resellerId) => {
    try {
      await api.put(`/customers/${c.id}`, { reseller_id: resellerId || "" });
      if (resellerId) {
        const r = resellers.find((x) => x.id === resellerId);
        toast.success(`Compte lié au revendeur ${r?.name || ""} ${r?.discount_percent != null ? `(−${r.discount_percent}%)` : ""}`);
      } else {
        toast.success("Lien revendeur supprimé");
      }
      load();
      if (selectedId === c.id) onOpenDetail(c);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const stats = data.stats || { total: 0, verified: 0, unverified: 0, disabled: 0 };

  return (
    <div className="space-y-6" data-testid="shop-customers-page">
      <div>
        <Link
          to="/parametres"
          className="inline-flex items-center gap-1 text-sm text-[#1F4E3D] hover:underline mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux paramètres
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-[#1F4E3D]" />
          Clients boutique B2C
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Comptes créés via le formulaire d'inscription boutique. Les anciennes commandes
          passées sans compte (mode invité historique) n'apparaissent pas ici.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total comptes" value={stats.total} testid="stat-total" />
        <StatCard
          label="Vérifiés"
          value={stats.verified}
          accent="text-emerald-700"
          testid="stat-verified"
        />
        <StatCard
          label="Non vérifiés"
          value={stats.unverified}
          accent="text-amber-700"
          testid="stat-unverified"
        />
        <StatCard
          label="Désactivés"
          value={stats.disabled}
          accent="text-gray-500"
          testid="stat-disabled"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Rechercher par email ou nom…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="customers-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <FilterChip active={!statusFilter} onClick={() => setStatusFilter(null)} testid="filter-all">
            Tous
          </FilterChip>
          <FilterChip active={statusFilter === "verified"} onClick={() => setStatusFilter("verified")} testid="filter-verified">
            Vérifiés
          </FilterChip>
          <FilterChip active={statusFilter === "unverified"} onClick={() => setStatusFilter("unverified")} testid="filter-unverified">
            Non vérifiés
          </FilterChip>
          <FilterChip active={statusFilter === "disabled"} onClick={() => setStatusFilter("disabled")} testid="filter-disabled">
            Désactivés
          </FilterChip>
        </div>
        {/* Phase 101cg — Filtre type de compte : par défaut on affiche que les
            particuliers pour ne pas mélanger avec les candidatures pro. */}
        <div className="flex gap-2 flex-wrap items-center pl-2 border-l border-[#E5E5E0]">
          <span className="text-[11px] uppercase tracking-wider text-[#575A57]">Type</span>
          <FilterChip active={typeFilter === "particulier"} onClick={() => setTypeFilter("particulier")} testid="filter-type-particulier">
            Particuliers
          </FilterChip>
          <FilterChip active={typeFilter === "pro"} onClick={() => setTypeFilter("pro")} testid="filter-type-pro">
            Pros
          </FilterChip>
          <FilterChip active={typeFilter === "all"} onClick={() => setTypeFilter("all")} testid="filter-type-all">
            Tous
          </FilterChip>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : data.customers.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="customers-empty">
            Aucun client {search ? "ne correspond à votre recherche" : "pour le moment"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Téléphone</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Inscrit le</th>
                  <th className="text-center px-4 py-3">Statut</th>
                  <th className="text-right px-4 py-3">Commandes</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.customers.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onOpenDetail(c)}
                    data-testid={`customer-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {c.name}
                        {c.reseller_id && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EAF3EE] text-[#1F4E3D]" title="Compte revendeur B2B">
                            <Briefcase className="w-2.5 h-2.5" /> B2B
                          </span>
                        )}
                        {c.account_type === "pro" && !c.reseller_id && c.pro_status === "pending" && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#FCEEEA] text-[#C05C47]" title="Candidature revendeur en attente — voir /revendeurs/candidatures">
                            <Briefcase className="w-2.5 h-2.5" /> Pro · En attente
                          </span>
                        )}
                        {c.account_type === "pro" && !c.reseller_id && c.pro_status === "rejected" && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F0F0EB] text-[#A3A6A3]" title="Candidature revendeur refusée">
                            <Briefcase className="w-2.5 h-2.5" /> Pro · Refusée
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{c.email}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-gray-600">{c.phone || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                      {fmtDateTime(c.created_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CustomerStatusBadge c={c} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-gray-700">
                        <ShoppingBag className="w-3.5 h-3.5" /> {c.orders_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {!c.email_verified && !c.disabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => onVerify(c)}
                          title="Vérifier manuellement"
                          data-testid={`customer-verify-${c.id}`}
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-600 hover:bg-gray-100"
                        onClick={() => onToggleDisabled(c)}
                        title={c.disabled ? "Réactiver" : "Désactiver"}
                        data-testid={`customer-toggle-${c.id}`}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(c)}
                        title="Supprimer"
                        data-testid={`customer-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer détail */}
      <Sheet open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="sm:max-w-xl w-[95vw] overflow-y-auto" data-testid="customer-detail-sheet">
          <SheetHeader>
            <SheetTitle>Détail du client</SheetTitle>
            <SheetDescription>Profil + historique des commandes</SheetDescription>
          </SheetHeader>
          {loadingDetail ? (
            <div className="py-8 flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : detail ? (
            <CustomerDetailView
              detail={detail}
              resellers={resellers}
              onVerify={onVerify}
              onToggleDisabled={onToggleDisabled}
              onDelete={onDelete}
              onLinkReseller={onLinkReseller}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatCard({ label, value, accent = "text-gray-900", testid }) {
  return (
    <div className="bg-white rounded-lg border p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent}`}>{value}</p>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-xs uppercase tracking-wide rounded-full border transition-colors ${
        active
          ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
          : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
      }`}
    >
      {children}
    </button>
  );
}

function CustomerStatusBadge({ c }) {
  if (c.disabled) {
    return <Badge variant="secondary" className="text-gray-500">Désactivé</Badge>;
  }
  if (c.email_verified) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Vérifié
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
      <XCircle className="w-3 h-3 mr-1" /> Non vérifié
    </Badge>
  );
}

function CustomerDetailView({ detail, resellers, onVerify, onToggleDisabled, onDelete, onLinkReseller }) {
  const c = detail.customer;
  const orders = detail.orders || [];
  const linkedReseller = detail.reseller;
  const totalSpent = useMemo(
    () => orders.filter((o) => o.status === "PAYEE").reduce((sum, o) => sum + (o.total_ttc || 0), 0),
    [orders]
  );
  // Liste des revendeurs sélectionnables (filtre ceux déjà liés à un autre client)
  const availableResellers = useMemo(() => resellers || [], [resellers]);

  return (
    <div className="mt-4 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold">{c.name}</h3>
          <p className="text-sm text-gray-500">{c.email}</p>
          <div className="mt-2">
            <CustomerStatusBadge c={c} />
          </div>
        </div>
      </div>

      {/* Coordonnées */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
        {c.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" /> {c.phone}
          </div>
        )}
        <div className="flex items-start gap-2">
          <Mail className="w-4 h-4 text-gray-400 mt-0.5" /> {c.email}
        </div>
        {(c.address || c.postal_code || c.city) && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
            <span>
              {c.address}
              {c.address && (c.postal_code || c.city) && <br />}
              {c.postal_code} {c.city}
            </span>
          </div>
        )}
      </div>

      {/* Méta */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <MetaItem label="Inscrit le" value={fmtDateTime(c.created_at)} />
        <MetaItem label="Dernière connexion" value={c.last_login_at ? fmtDateTime(c.last_login_at) : "—"} />
        <MetaItem label="Commandes" value={orders.length} />
        <MetaItem label="Total dépensé" value={`${totalSpent.toFixed(2)} CHF`} accent="text-emerald-700" />
      </div>

      {/* Phase 31 : Revendeur lié */}
      <div className="bg-[#FAFAF8] border rounded-lg p-4 space-y-3" data-testid="customer-reseller-section">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <Briefcase className="w-4 h-4 text-[#1F4E3D]" />
            Statut revendeur B2B
          </h4>
          {linkedReseller && linkedReseller.discount_percent != null && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#EAF3EE] text-[#1F4E3D]">
              −{linkedReseller.discount_percent}%
            </span>
          )}
        </div>
        {linkedReseller ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3 bg-white border rounded p-2.5">
              <div className="min-w-0">
                <div className="font-medium text-[#1F4E3D]">{linkedReseller.name}</div>
                {linkedReseller.legal_name && (
                  <div className="text-xs text-gray-500">{linkedReseller.legal_name}</div>
                )}
                {linkedReseller.archived && (
                  <div className="text-[10px] text-amber-700 mt-0.5">⚠ Revendeur archivé — le rabais n'est plus appliqué</div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Link
                  to={`/revendeurs/${linkedReseller.id}`}
                  className="text-xs text-[#1F4E3D] hover:underline whitespace-nowrap"
                >
                  Voir →
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-red-600 h-7 px-2"
                  onClick={() => onLinkReseller(c, "")}
                  title="Délier"
                  data-testid="detail-unlink-reseller"
                >
                  <Unlink className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-600">
              Ce client n'est pas (encore) un revendeur B2B. Liez-le à une fiche revendeur pour appliquer ses prix négociés.
            </p>
            <div className="flex items-center gap-2">
              <Select onValueChange={(v) => onLinkReseller(c, v)}>
                <SelectTrigger className="flex-1 h-9 bg-white" data-testid="reseller-select">
                  <SelectValue placeholder="Sélectionner un revendeur…" />
                </SelectTrigger>
                <SelectContent>
                  {availableResellers.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-gray-500 text-center">
                      Aucun revendeur actif.<br />
                      <Link to="/revendeurs" className="text-[#1F4E3D] hover:underline text-xs">→ Créer un revendeur</Link>
                    </div>
                  ) : (
                    availableResellers.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <span className="flex items-center gap-2">
                          <Link2 className="w-3 h-3 text-gray-400" />
                          {r.name}
                          {r.discount_percent != null && (
                            <span className="text-xs text-[#1F4E3D]">−{r.discount_percent}%</span>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {availableResellers.length > 0 && (
              <p className="text-[10px] text-gray-500">
                💡 Astuce : si le revendeur a un email renseigné qui correspond à celui du compte client, le lien est créé <strong>automatiquement</strong> à l'inscription.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {!c.email_verified && !c.disabled && (
          <Button
            variant="outline"
            size="sm"
            className="text-emerald-700 border-emerald-700"
            onClick={() => onVerify(c)}
            data-testid="detail-verify"
          >
            <UserCheck className="w-4 h-4 mr-1" /> Vérifier manuellement
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleDisabled(c)}
          data-testid="detail-toggle"
        >
          <Power className="w-4 h-4 mr-1" /> {c.disabled ? "Réactiver" : "Désactiver"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 border-red-600 hover:bg-red-50"
          onClick={() => onDelete(c)}
          data-testid="detail-delete"
        >
          <Trash2 className="w-4 h-4 mr-1" /> Supprimer
        </Button>
      </div>

      {/* Historique commandes */}
      <div>
        <h4 className="text-sm font-semibold mb-3">Historique des commandes</h4>
        {orders.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Aucune commande.</p>
        ) : (
          <ul className="space-y-2">
            {orders.map((o) => (
              <li key={o.id} data-testid={`detail-order-${o.id}`}>
                <Link
                  to={`/commandes-clients/${o.id}`}
                  className="bg-white border rounded p-3 flex items-center justify-between gap-3 hover:bg-[#FAF6EE] hover:border-[#1F4E3D] transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-semibold text-[#1F4E3D]">{o.reference} →</div>
                    <div className="text-xs text-gray-500">
                      {fmtDateTime(o.created_at)} · {o.status}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#1F4E3D]">
                    {(o.total_ttc || 0).toFixed(2)} CHF
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MetaItem({ label, value, accent = "text-gray-900" }) {
  return (
    <div className="bg-white border rounded p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-0.5 font-semibold ${accent}`}>{value}</div>
    </div>
  );
}
